import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Calendar } from './ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { RadioGroup, RadioGroupItem } from './ui/radio-group';
import { Calendar as CalendarIcon, Languages, ArrowLeft } from 'lucide-react';
import { format } from 'date-fns';
import { properties } from '../data/properties';
import { toast } from '../hooks/use-toast';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Translation content
const translations = {
  en: {
    title: 'Resident Pre-Qualification Application',
    subtitle: 'Free pre-qualification to help you lease a managed property',
    property: 'Which property are you interested in?',
    propertyPlaceholder: 'Select a property',
    contactInfo: 'Contact Information',
    fullName: 'Full Name',
    email: 'Email Address',
    phone: 'Phone Number',
    householdInfo: 'Household Information',
    monthlyIncome: 'Total Monthly Household Income',
    incomePlaceholder: 'Combined income of all parties on lease',
    numCars: 'Number of Vehicles',
    carsPlaceholder: 'How many cars need parking?',
    employment: 'Type of Work',
    employmentPlaceholder: 'What type of work do you do?',
    moveInDate: 'Desired Move-In Date',
    selectDate: 'Select a date',
    financialInfo: 'Financial Information',
    creditScore: 'Estimated Credit Score',
    currentRent: 'Current Monthly Rent',
    currentRentPlaceholder: 'How much do you currently pay?',
    leaseLength: 'Length at Current Property',
    leaseLengthPlaceholder: 'How long have you lived there?',
    oweMoney: 'Do you owe your current landlord any money?',
    yes: 'Yes',
    no: 'No',
    eviction: 'Has an eviction ever been filed against you?',
    pets: 'Do you have any pets?',
    additionalInfo: 'Additional Details',
    additionalPlaceholder: 'Any additional information you would like to provide...',
    tourScheduling: 'Schedule a Tour',
    tourDescription: 'Select preferred times for a property tour',
    tourDate: 'Preferred Tour Date',
    submit: 'Submit Pre-Qualification',
    submitting: 'Submitting...',
    backHome: 'Back to Home'
  },
  es: {
    title: 'Solicitud de Precalificación de Residente',
    subtitle: 'Precalificación gratuita para ayudarle a arrendar una propiedad administrada',
    property: '¿Qué propiedad le interesa?',
    propertyPlaceholder: 'Seleccione una propiedad',
    contactInfo: 'Información de Contacto',
    fullName: 'Nombre Completo',
    email: 'Correo Electrónico',
    phone: 'Número de Teléfono',
    householdInfo: 'Información del Hogar',
    monthlyIncome: 'Ingreso Mensual Total del Hogar',
    incomePlaceholder: 'Ingreso combinado de todas las partes en el contrato',
    numCars: 'Número de Vehículos',
    carsPlaceholder: '¿Cuántos autos necesitan estacionamiento?',
    employment: 'Tipo de Trabajo',
    employmentPlaceholder: '¿Qué tipo de trabajo hace?',
    moveInDate: 'Fecha Deseada de Mudanza',
    selectDate: 'Seleccione una fecha',
    financialInfo: 'Información Financiera',
    creditScore: 'Puntuación de Crédito Estimada',
    currentRent: 'Renta Mensual Actual',
    currentRentPlaceholder: '¿Cuánto paga actualmente?',
    leaseLength: 'Tiempo en la Propiedad Actual',
    leaseLengthPlaceholder: '¿Cuánto tiempo ha vivido allí?',
    oweMoney: '¿Le debe dinero a su propietario actual?',
    yes: 'Sí',
    no: 'No',
    eviction: '¿Alguna vez se ha presentado un desalojo contra usted?',
    pets: '¿Tiene mascotas?',
    additionalInfo: 'Detalles Adicionales',
    additionalPlaceholder: 'Cualquier información adicional que desee proporcionar...',
    tourScheduling: 'Programar una Visita',
    tourDescription: 'Seleccione horarios preferidos para una visita a la propiedad',
    tourDate: 'Fecha Preferida para la Visita',
    submit: 'Enviar Precalificación',
    submitting: 'Enviando...',
    backHome: 'Volver al Inicio'
  }
};

const PreQualification = () => {
  const navigate = useNavigate();
  const [language, setLanguage] = useState('en');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [moveInDate, setMoveInDate] = useState();
  const [tourDate, setTourDate] = useState();
  
  const [formData, setFormData] = useState({
    property: '',
    fullName: '',
    email: '',
    phone: '',
    monthlyIncome: '',
    numCars: '',
    employment: '',
    creditScore: '',
    currentRent: '',
    leaseLength: '',
    oweMoney: 'no',
    eviction: 'no',
    pets: 'no',
    additionalInfo: ''
  });

  const t = translations[language];

  const toggleLanguage = () => {
    setLanguage(language === 'en' ? 'es' : 'en');
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const submissionData = {
        ...formData,
        moveInDate: moveInDate ? format(moveInDate, 'PPP') : '',
        tourDate: tourDate ? format(tourDate, 'PPP') : '',
        language: language,
        submittedAt: new Date().toISOString()
      };

      const response = await axios.post(`${API}/pre-qualification`, submissionData);

      toast({
        title: language === 'en' ? "Application Submitted!" : "¡Solicitud Enviada!",
        description: language === 'en' 
          ? "We'll review your application and contact you soon."
          : "Revisaremos su solicitud y nos comunicaremos pronto.",
      });

      // Reset form
      setFormData({
        property: '',
        fullName: '',
        email: '',
        phone: '',
        monthlyIncome: '',
        numCars: '',
        employment: '',
        creditScore: '',
        currentRent: '',
        leaseLength: '',
        oweMoney: 'no',
        eviction: 'no',
        pets: 'no',
        additionalInfo: ''
      });
      setMoveInDate(undefined);
      setTourDate(undefined);

    } catch (error) {
      console.error('Submission error:', error);
      toast({
        title: language === 'en' ? "Error" : "Error",
        description: language === 'en'
          ? "Failed to submit application. Please try again."
          : "Error al enviar la solicitud. Por favor, inténtelo de nuevo.",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header with Language Toggle */}
        <div className="mb-8">
          <Button
            variant="ghost"
            onClick={() => navigate('/')}
            className="mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            {t.backHome}
          </Button>
          
          <div className="flex justify-between items-start">
            <div>
              <h1 className="text-4xl font-bold text-gray-900 mb-2">{t.title}</h1>
              <p className="text-lg text-gray-600">{t.subtitle}</p>
            </div>
            <Button
              onClick={toggleLanguage}
              size="lg"
              className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-8 py-6 text-lg font-semibold shadow-lg"
            >
              <Languages className="w-6 h-6" />
              {language === 'en' ? 'Español' : 'English'}
            </Button>
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          {/* Property Selection */}
          <Card className="mb-6">
            <CardHeader>
              <CardTitle>{t.property}</CardTitle>
            </CardHeader>
            <CardContent>
              <Select name="property" value={formData.property} onValueChange={(value) => setFormData({...formData, property: value})}>
                <SelectTrigger>
                  <SelectValue placeholder={t.propertyPlaceholder} />
                </SelectTrigger>
                <SelectContent>
                  {properties.map((property) => (
                    <SelectItem key={property.id} value={property.id}>
                      {property.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </CardContent>
          </Card>

          {/* Contact Information */}
          <Card className="mb-6">
            <CardHeader>
              <CardTitle>{t.contactInfo}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="fullName">{t.fullName}</Label>
                <Input
                  id="fullName"
                  name="fullName"
                  value={formData.fullName}
                  onChange={handleChange}
                  required
                />
              </div>
              <div>
                <Label htmlFor="email">{t.email}</Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                />
              </div>
              <div>
                <Label htmlFor="phone">{t.phone}</Label>
                <Input
                  id="phone"
                  name="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                />
              </div>
            </CardContent>
          </Card>

          {/* Household Information */}
          <Card className="mb-6">
            <CardHeader>
              <CardTitle>{t.householdInfo}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="monthlyIncome">{t.monthlyIncome}</Label>
                <Input
                  id="monthlyIncome"
                  name="monthlyIncome"
                  type="text"
                  placeholder={t.incomePlaceholder}
                  value={formData.monthlyIncome}
                  onChange={handleChange}
                  required
                />
              </div>
              <div>
                <Label htmlFor="numCars">{t.numCars}</Label>
                <Input
                  id="numCars"
                  name="numCars"
                  type="number"
                  placeholder={t.carsPlaceholder}
                  value={formData.numCars}
                  onChange={handleChange}
                  required
                />
              </div>
              <div>
                <Label htmlFor="employment">{t.employment}</Label>
                <Input
                  id="employment"
                  name="employment"
                  placeholder={t.employmentPlaceholder}
                  value={formData.employment}
                  onChange={handleChange}
                  required
                />
              </div>
              <div>
                <Label>{t.moveInDate}</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-full justify-start text-left font-normal">
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {moveInDate ? format(moveInDate, 'PPP') : <span>{t.selectDate}</span>}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0">
                    <Calendar mode="single" selected={moveInDate} onSelect={setMoveInDate} initialFocus />
                  </PopoverContent>
                </Popover>
              </div>
            </CardContent>
          </Card>

          {/* Financial Information */}
          <Card className="mb-6">
            <CardHeader>
              <CardTitle>{t.financialInfo}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="creditScore">{t.creditScore}</Label>
                <Select name="creditScore" value={formData.creditScore} onValueChange={(value) => setFormData({...formData, creditScore: value})}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select range" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="excellent">Excellent (750+)</SelectItem>
                    <SelectItem value="good">Good (700-749)</SelectItem>
                    <SelectItem value="fair">Fair (650-699)</SelectItem>
                    <SelectItem value="poor">Poor (600-649)</SelectItem>
                    <SelectItem value="verypoor">Very Poor (Below 600)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="currentRent">{t.currentRent}</Label>
                <Input
                  id="currentRent"
                  name="currentRent"
                  placeholder={t.currentRentPlaceholder}
                  value={formData.currentRent}
                  onChange={handleChange}
                  required
                />
              </div>
              <div>
                <Label htmlFor="leaseLength">{t.leaseLength}</Label>
                <Input
                  id="leaseLength"
                  name="leaseLength"
                  placeholder={t.leaseLengthPlaceholder}
                  value={formData.leaseLength}
                  onChange={handleChange}
                  required
                />
              </div>
              <div>
                <Label>{t.oweMoney}</Label>
                <RadioGroup name="oweMoney" value={formData.oweMoney} onValueChange={(value) => setFormData({...formData, oweMoney: value})}>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="yes" id="owe-yes" />
                    <Label htmlFor="owe-yes">{t.yes}</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="no" id="owe-no" />
                    <Label htmlFor="owe-no">{t.no}</Label>
                  </div>
                </RadioGroup>
              </div>
              <div>
                <Label>{t.eviction}</Label>
                <RadioGroup name="eviction" value={formData.eviction} onValueChange={(value) => setFormData({...formData, eviction: value})}>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="yes" id="eviction-yes" />
                    <Label htmlFor="eviction-yes">{t.yes}</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="no" id="eviction-no" />
                    <Label htmlFor="eviction-no">{t.no}</Label>
                  </div>
                </RadioGroup>
              </div>
              <div>
                <Label>{t.pets}</Label>
                <RadioGroup name="pets" value={formData.pets} onValueChange={(value) => setFormData({...formData, pets: value})}>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="yes" id="pets-yes" />
                    <Label htmlFor="pets-yes">{t.yes}</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="no" id="pets-no" />
                    <Label htmlFor="pets-no">{t.no}</Label>
                  </div>
                </RadioGroup>
              </div>
            </CardContent>
          </Card>

          {/* Additional Information */}
          <Card className="mb-6">
            <CardHeader>
              <CardTitle>{t.additionalInfo}</CardTitle>
            </CardHeader>
            <CardContent>
              <Textarea
                name="additionalInfo"
                placeholder={t.additionalPlaceholder}
                value={formData.additionalInfo}
                onChange={handleChange}
                rows={4}
              />
            </CardContent>
          </Card>

          {/* Tour Scheduling */}
          <Card className="mb-6">
            <CardHeader>
              <CardTitle>{t.tourScheduling}</CardTitle>
              <CardDescription>{t.tourDescription}</CardDescription>
            </CardHeader>
            <CardContent>
              <Label>{t.tourDate}</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-full justify-start text-left font-normal">
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {tourDate ? format(tourDate, 'PPP') : <span>{t.selectDate}</span>}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0">
                  <Calendar mode="single" selected={tourDate} onSelect={setTourDate} initialFocus />
                </PopoverContent>
              </Popover>
            </CardContent>
          </Card>

          {/* Submit Button */}
          <Button
            type="submit"
            disabled={isSubmitting}
            className="w-full bg-blue-600 hover:bg-blue-700 text-white py-6 text-lg"
          >
            {isSubmitting ? t.submitting : t.submit}
          </Button>
        </form>
      </div>
    </div>
  );
};

export default PreQualification;